import {take} from 'lodash';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {useInitialEffect} from '../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {parseTeamNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {DataEventActionsEnum} from '../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {AnalyticsDataEventObject} from '../../../../libs/shared/core/src/models/tracking/objectNames/analyticsDataEventObject';
import {getHasLiveDashboardWaitingMetrics} from '../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {AnalyticsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {AnalyticsFilterTypesEnum} from '../../../models/api/analyticsSavedViewModel';
import {makeGetUserParticipatingInboxIdsByTeamNamespace} from '../../../store/data/inboxes/inboxesSelectors';
import {makeGetVisibleInboxIdsForTeam} from '../../../store/data/workspace/workspaceSelectors';
import {makeGetAnalyticsFilter} from '../../../store/panels/analytics/filters/analyticsPanelFiltersSelectors';
import {AnalyticsParameters} from '../../../store/panels/analytics/parameters/analyticsParameters';
import {
  makeGetAnalyticsLiveDashboardContent,
  makeGetAnalyticsLiveDashboardError,
} from '../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {useAnalyticsFilter} from '../../helpers/hooks/useAnalyticsFilter';
import {useAnalyticsTrackingFrontendEvent} from '../../helpers/tracking/useAnalyticsTracking';
import {AnalyticsErrorState} from '../reportElements/analyticsErrorState';
import {
  LIVE_DASHBOARD_MAX_DEFAULT_INBOXES,
  LIVE_DASHBOARD_MIN_OUTER_WIDTH,
  LIVE_DASHBOARD_REDUCED_GAP_BREAKPOINT_VIEWPORT_WIDTH,
} from './constants';
import {LiveDashboardContent} from './liveDashboardContent';
import {LiveDashboardHeader} from './liveDashboardHeader';
import {LiveDashboardNoInboxesMessage} from './liveDashboardNoInboxesMessage';

/*
 * Props.
 */

interface LiveDashboardProps {
  panelId: AnalyticsPanelId;
  parameters: AnalyticsParameters;
  isThisPoppedOut: boolean;
  isLiveDashboardPoppedOut: boolean;
  isBetaBannerOpen: boolean;
}

/*
 * Styles.
 */

const StyledWrapperWrapperDiv = styled.div`
  overflow-y: auto;
  min-width: ${LIVE_DASHBOARD_MIN_OUTER_WIDTH}px;

  /*
    The following matters when popped-out as we want
    the grey background to go to the bottom of the viewport.
  */
  height: 100%;
`;

const StyledWrapperDiv = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  height: 100%;

  overflow-y: hidden;
`;

const StyledHeaderWrapperDiv = styled.div`
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  padding: 32px 48px 24px 48px;

  @media screen and (max-width: ${LIVE_DASHBOARD_REDUCED_GAP_BREAKPOINT_VIEWPORT_WIDTH}px) {
    padding: 32px 12px 24px 12px;
  }

  overflow-y: auto;
  scrollbar-gutter: stable;
`;

const StyledGridWrapperDiv = styled.div`
  padding: 24px 48px;
  background-color: ${(p) => p.theme.greys.shade20};
  overflow-y: auto;
  scrollbar-gutter: stable;

  @media screen and (max-width: ${LIVE_DASHBOARD_REDUCED_GAP_BREAKPOINT_VIEWPORT_WIDTH}px) {
    padding: 12px 12px 0 12px;
  }
`;

export const LiveDashboard: FC<LiveDashboardProps> = ({
  panelId,
  parameters,
  isThisPoppedOut,
  isLiveDashboardPoppedOut,
  isBetaBannerOpen,
}) => {
  const {namespace} = parameters;
  const getPanelId = useMemo(() => () => panelId, [panelId]);

  const [getAnalyticsLiveDashboardContent, getAnalyticsLiveDashboardError] = useMemo(
    () => [
      makeGetAnalyticsLiveDashboardContent(() => panelId),
      makeGetAnalyticsLiveDashboardError(() => panelId),
    ],
    [panelId],
  );

  const trackAnalyticsFrontendEvent = useAnalyticsTrackingFrontendEvent(getPanelId);
  const liveDashboardContent = useAppSelector(getAnalyticsLiveDashboardContent);
  const liveDashboardError = useAppSelector(getAnalyticsLiveDashboardError);

  const [selectedInboxesFilterValue, setFilterValue] = useAnalyticsFilter(
    AnalyticsFilterTypesEnum.LIVE_DASHBOARD_INBOXES,
    getPanelId,
  );

  const getHasWaitingMetrics = useMemo(
    () => makeGetAnalyticsFilter(AnalyticsFilterTypesEnum.LIVE_DASHBOARD_WITH_WAITING, getPanelId),
    [getPanelId],
  );

  const selectedInboxIds = selectedInboxesFilterValue ?? [];
  const informedDefaultInboxSelection = useLiveDashboardDefaultInboxSelection(namespace);

  const hasWaitingMetricsFeature = useAppSelector(getHasLiveDashboardWaitingMetrics);

  // This controls if we are showing Waiting metrics across every section of the dashboard.
  const hasWaitingMetrics = (useAppSelector(getHasWaitingMetrics) ?? false) && hasWaitingMetricsFeature;

  // If no inboxes are selected, default to inboxes pinned in the user's P1.
  useInitialEffect(() => {
    if (
      (!selectedInboxesFilterValue || selectedInboxesFilterValue.length === 0) &&
      informedDefaultInboxSelection.length > 0
    ) {
      setFilterValue(informedDefaultInboxSelection);
    }
  });

  useInitialEffect(() => {
    if (isThisPoppedOut) {
      trackAnalyticsFrontendEvent(DataEventActionsEnum.VIEW, AnalyticsDataEventObject.POPOUT);
    } else {
      trackAnalyticsFrontendEvent(DataEventActionsEnum.VIEW, AnalyticsDataEventObject.REPORT, {});
      trackAnalyticsFrontendEvent(DataEventActionsEnum.VIEW, AnalyticsDataEventObject.VIEW, {});
    }
  });

  const renderBody = () => {
    if (selectedInboxIds.length === 0) {
      return <LiveDashboardNoInboxesMessage />;
    }

    if (liveDashboardError) {
      return <AnalyticsErrorState panelId={panelId} copyableErrorString={liveDashboardError} />;
    }

    return (
      <LiveDashboardContent
        panelId={panelId}
        selectedInboxIds={selectedInboxIds}
        hasWaitingMetrics={hasWaitingMetrics}
        liveDashboardContent={liveDashboardContent}
        isThisPoppedOut={isThisPoppedOut}
        isBetaBannerOpen={isBetaBannerOpen}
      />
    );
  };

  return (
    <StyledWrapperWrapperDiv>
      <StyledWrapperDiv>
        <StyledHeaderWrapperDiv>
          <LiveDashboardHeader
            panelId={panelId}
            namespace={namespace}
            reportFetchedAtMillis={liveDashboardContent?.updatedAt}
            isLiveDashboardPoppedOut={isLiveDashboardPoppedOut}
          />
        </StyledHeaderWrapperDiv>
        <StyledGridWrapperDiv>{renderBody()}</StyledGridWrapperDiv>
      </StyledWrapperDiv>
    </StyledWrapperWrapperDiv>
  );
};

export function useLiveDashboardDefaultInboxSelection(namespace: string) {
  const teamId = parseTeamNamespace(namespace);

  // Using an invalid team id here will simply return an empty array.
  const getVisibleInboxIdsForTeam = useMemo(() => makeGetVisibleInboxIdsForTeam(teamId ?? 0), [teamId]);
  const getUserParticipatingInboxIdsByTeamNamespace = useMemo(
    () => makeGetUserParticipatingInboxIdsByTeamNamespace<void>(() => [namespace]),
    [namespace],
  );

  const p1Inboxes = useAppSelector(getVisibleInboxIdsForTeam);

  // Sorted alphabetically.
  const userParticipatingInboxIdsByTeamNamespace = useAppSelector(
    getUserParticipatingInboxIdsByTeamNamespace,
  );

  return useMemo(() => {
    if (p1Inboxes.size > 0) {
      return take([...p1Inboxes], LIVE_DASHBOARD_MAX_DEFAULT_INBOXES);
    }

    // If the user has not pinned any inboxes, use the first team inboxes.
    const userParticipatingInboxIds = userParticipatingInboxIdsByTeamNamespace[namespace] ?? [];
    return take(userParticipatingInboxIds, LIVE_DASHBOARD_MAX_DEFAULT_INBOXES);
  }, [namespace, p1Inboxes, userParticipatingInboxIdsByTeamNamespace]);
}
